import { useCallback, useEffect, useMemo } from "react";
import { t } from "ttag";

import {
  PermissionsEditor,
  PermissionsEditorEmptyState,
} from "metabase/admin/permissions/components/PermissionsEditor";
import { PermissionsPageLayout } from "metabase/admin/permissions/components/PermissionsPageLayout";
import { useListCollectionsTreeQuery } from "metabase/api";
import { getPermissionsBasePath } from "metabase/common/components/PermissionsBasePath/base-path";
import {
  CollectionPermissionsHelp,
  type PermissionEditorEntity,
  PermissionsSidebar,
  type UpdateTenantCollectionPermissionParams,
  assertNumericId,
  initializeTenantSpecificCollectionPermissions,
  loadTenantSpecificCollectionPermissions,
  saveTenantSpecificCollectionPermissions,
  updateTenantSpecificCollectionPermission,
} from "metabase/permissions";
import { useDispatch, useSelector } from "metabase/redux";
import { useNavigate, useParams } from "metabase/router";
import type { Collection, CollectionId } from "metabase-types/api";

import {
  getIsTenantSpecificDirty,
  getTenantSpecificCollectionEntity,
  getTenantSpecificCollectionsPermissionEditor,
  getTenantSpecificCollectionsSidebar,
  tenantSpecificCollectionsQuery,
} from "./selectors";

function TenantSpecificCollectionPermissionsPageView() {
  const { collectionId } = useParams();
  useListCollectionsTreeQuery(tenantSpecificCollectionsQuery);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const props = useMemo(() => ({ params: { collectionId } }), [collectionId]);
  const sidebar = useSelector((state) =>
    getTenantSpecificCollectionsSidebar(state, props),
  );
  const permissionEditor = useSelector((state) =>
    getTenantSpecificCollectionsPermissionEditor(state, props),
  );
  const isDirty = useSelector(getIsTenantSpecificDirty);
  const collection = useSelector((state) =>
    getTenantSpecificCollectionEntity(state, props),
  );

  const initialize = useCallback(() => {
    dispatch(initializeTenantSpecificCollectionPermissions());
  }, [dispatch]);

  const loadPermissions = useCallback(() => {
    dispatch(loadTenantSpecificCollectionPermissions());
  }, [dispatch]);

  const savePermissions = useCallback(() => {
    dispatch(saveTenantSpecificCollectionPermissions());
  }, [dispatch]);

  const navigateToItem = useCallback(
    ({ id }: { id: CollectionId }) => {
      navigate(`${getPermissionsBasePath()}/tenant-specific-collections/${id}`);
    },
    [navigate],
  );

  const updateCollectionPermission = useCallback(
    ({
      groupId,
      collection,
      value,
      shouldPropagateToChildren,
    }: UpdateTenantCollectionPermissionParams) => {
      dispatch(
        updateTenantSpecificCollectionPermission({
          groupId,
          collection,
          value,
          shouldPropagateToChildren,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handlePermissionChange = useCallback(
    (
      item: PermissionEditorEntity,
      _permission: unknown,
      value: unknown,
      _toggleState: boolean | null,
    ) => {
      if (!collection) {
        return;
      }
      // Always propagate to sub-collections for tenant-specific collections
      updateCollectionPermission({
        groupId: assertNumericId(item.id),
        // Unjustified type cast. FIXME
        collection: collection as Collection,
        value,
        shouldPropagateToChildren: true,
      });
    },
    [collection, updateCollectionPermission],
  );

  return (
    <PermissionsPageLayout
      tab="tenant-specific-collections"
      isDirty={isDirty}
      onSave={savePermissions}
      onLoad={() => loadPermissions()}
      helpContent={<CollectionPermissionsHelp />}
    >
      <PermissionsSidebar {...sidebar} onSelect={navigateToItem} />

      {!permissionEditor && (
        <PermissionsEditorEmptyState
          icon="folder"
          message={t`Select a collection to see its permissions`}
        />
      )}

      {permissionEditor && (
        <PermissionsEditor
          isLoading={undefined}
          error={undefined}
          {...permissionEditor}
          onChange={handlePermissionChange}
        />
      )}
    </PermissionsPageLayout>
  );
}

export const TenantSpecificCollectionPermissionsPage =
  TenantSpecificCollectionPermissionsPageView;
